// Preserve video playback position across a VideoJs remount.
//
// Rotating between portrait and landscape swaps the Experience layout, which
// unmounts and remounts the <Video> component — disposing the Video.js player
// and recreating it from 0. This module stashes the position + play state on
// unmount and restores them on the next mount, but ONLY for the same
// experience: a chain advance loads a different idexperience and must start at
// the beginning, so a mismatched id yields nothing (and clears the stash).

export interface SavedPlayback {
  idexperience: unknown;
  time: number;
  playing: boolean;
  muted: boolean;
}

let _saved: SavedPlayback | null = null;

export function save(
  idexperience: unknown,
  time: number,
  playing: unknown,
  muted: unknown,
) {
  if (idexperience == null) {
    return;
  }
  // Keep the play-state even at time 0. A fast rotate while the source is still
  // reloading can leave currentTime at ~0; dropping the stash there (the old
  // `!(time > 0)` bail) is what let playback silently stop after a couple of
  // quick rotations. Still skip a genuinely idle player (paused at 0).
  if (!(time > 0) && !playing) {
    return;
  }
  _saved = {
    idexperience,
    time: time > 0 ? time : 0,
    playing: !!playing,
    muted: !!muted,
  };
}

export function consume(idexperience: unknown): SavedPlayback | null {
  if (_saved && _saved.idexperience === idexperience) {
    const saved = _saved;
    _saved = null;
    return saved;
  }
  // A different experience mounted — the stash is stale; drop it.
  _saved = null;
  return null;
}
